import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import logo from '../assets/icons/full-logo.png';
import loginImage from '../assets/images/loginImage.png';
import { loginApi } from "../services/auth";
import { toast } from "react-toastify";
import InputField from "../components/inputFields/InputField";
import Button from "../components/buttons/Button";
import { HiShieldCheck, HiLockClosed, HiMail, HiArrowRight } from 'react-icons/hi';
import {
    getPostLoginPath,
    getUserRole,
    isAllowedDashboardLogin,
} from "../helpers/local_storage_helpers";

const Signin = () => {
    const navigate = useNavigate();
    const [hidePassword, setHidePassword] = useState(true);
    const [showLoginForm, setShowLoginForm] = useState(false);

    useEffect(() => {
        // Animate login form appearance after a short delay
        const timer = setTimeout(() => {
            setShowLoginForm(true);
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const payload = {
                username: values.username,
                password: values.password,
            };
            const response = await loginApi(payload);

            const responseData = response?.data;
            const apiStatus = responseData?.status;
            const loginOk = apiStatus === 200 || apiStatus === "200";

            if (response?.status === 200) {
                if (loginOk) {
                    const token = responseData?.token;
                    const user = responseData?.user;

                    if (isAllowedDashboardLogin(user, token)) {
                        localStorage.setItem("auth_token", token);
                        localStorage.setItem("user", JSON.stringify(user));
                        toast.success("Login successful! Redirecting...", {
                            position: "top-center",
                            autoClose: 1500,
                        });
                        resetForm();
                        window.location.href = getPostLoginPath(user?.role);
                    } else {
                        toast.error("Unauthorized User.");
                    }
                } else {
                    toast.error(responseData.message);
                }
            } else {
                let errors = responseData.message || "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again."
            );
        }
        setSubmitting(false);
    };

    const togglePassword = () => {
        setHidePassword(!hidePassword);
    };

    useEffect(() => {
        const token = localStorage?.getItem("auth_token") || "";
        if (token.length > 1) {
            window.location.href = getPostLoginPath(getUserRole());
        }
    }, []);

    // Enhanced animations
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                when: "beforeChildren",
                staggerChildren: 0.2
            }
        }
    };

    const formVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 12,
                stiffness: 100
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const titleVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.3,
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const buttonVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                delay: 0.6,
                duration: 0.4,
                ease: "easeOut"
            }
        },
        hover: {
            scale: 1.03,
            boxShadow: "0px 10px 20px rgba(88, 80, 236, 0.3)",
            transition: {
                duration: 0.3,
                ease: "easeInOut"
            }
        },
        tap: {
            scale: 0.97
        }
    };

    const BubblesBackground = () => {
        const bubbles = Array.from({ length: 10 });

        return (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {bubbles.map((_, index) => (
                    <motion.div
                        key={index}
                        className="absolute rounded-full bg-gradient-to-r from-primary/10 to-indigo-400/10"
                        style={{
                            width: `${Math.random() * 200 + 50}px`,
                            height: `${Math.random() * 200 + 50}px`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        initial={{ opacity: 0.1, scale: 0.8 }}
                        animate={{
                            y: [0, Math.random() * -100 - 50],
                            x: [0, (Math.random() - 0.5) * 50],
                            opacity: [0.1, 0.3, 0.1],
                            scale: [0.8, 1.2, 0.9],
                            rotate: [0, Math.random() * 360],
                        }}
                        transition={{
                            duration: Math.random() * 20 + 15,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden relative">
            <BubblesBackground />

            {/* Security badge */}
            <motion.div
                className="absolute top-4 right-4 z-50 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <HiShieldCheck className="text-green-500 text-lg" />
                <span className="text-sm font-medium text-gray-700">Secure Login</span>
            </motion.div>

            <div className="flex min-h-screen">
                <motion.div
                    className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 relative z-10 min-h-screen lg:min-h-0"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <div className="w-full max-w-md relative">
                        {/* Enhanced decorative elements */}
                        <div className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-r from-primary/30 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                        <div className="absolute -bottom-20 -right-20 w-56 h-56 bg-gradient-to-l from-indigo-500/30 to-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

                        {/* Logo with enhanced animation */}
                        <motion.div className="text-center mb-6 sm:mb-8">
                            <motion.img
                                src={logo}
                                alt="Bazaar Seller Dashboard Logo"
                                className="h-16 sm:h-20 mx-auto drop-shadow-lg mb-3 sm:mb-4"
                                initial={{ y: -50, opacity: 0, scale: 0.8 }}
                                animate={{ y: 0, opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                            />

                            <motion.div variants={titleVariants}>
                                <h1 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-center bg-clip-text text-transparent bg-gradient-to-r from-primary via-blue-600 to-indigo-600 mb-2 leading-tight">
                                    Welcome Back
                                </h1>
                                <p className="text-gray-600 text-sm sm:text-base font-medium px-2">
                                    Sign in to access your dashboard
                                </p>
                            </motion.div>
                        </motion.div>

                        <AnimatePresence>
                            {showLoginForm && (
                                <motion.div
                                    variants={formVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="bg-white/95 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-2xl border border-white/20 relative overflow-hidden"
                                >
                                    {/* Form header with security indicator */}
                                    <div className="text-center mb-6">
                                        <div className="flex items-center justify-center gap-2 mb-3">
                                            <HiLockClosed className="text-gray-400 text-lg" />
                                            <span className="text-sm text-gray-500 font-medium">Secure Sign In</span>
                                        </div>
                                    </div>

                                    <Formik
                                        initialValues={{
                                            username: "",
                                            password: "",
                                            rememberMe: false,
                                        }}
                                        validationSchema={Yup.object({
                                            username: Yup.string()
                                                .required("username is required"),
                                            password: Yup.string()
                                                .min(6, "Password must be at least 6 characters")
                                                .required("Password is required"),
                                        })}
                                        onSubmit={handleFormSubmit}
                                    >
                                        {({ isSubmitting, values, errors, touched }) => (
                                            <Form className="space-y-6">
                                                {/* Email Field with Icon */}
                                                <motion.div variants={itemVariants}>
                                                    <div className="relative">
                                                        <InputField
                                                            label="Username"
                                                            name="username"
                                                            placeholder="Enter your username"
                                                            required
                                                            className="pl-12 py-4 rounded-2xl bg-gray-50/80 w-full border-gray-200 focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-300 text-base"
                                                        />
                                                        <div className="absolute bottom-0 left-4 top-6 flex items-center pointer-events-none">
                                                            <HiMail className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                    {/* Email validation indicator */}
                                                    {values.username && !errors.username && touched.username && (
                                                        <motion.div
                                                            className="flex items-center gap-1 mt-2 text-green-600"
                                                            initial={{ opacity: 0, scale: 0.8 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                        >
                                                            <HiShieldCheck className="h-4 w-4" />
                                                            <span className="text-sm">Valid username format</span>
                                                        </motion.div>
                                                    )}
                                                </motion.div>

                                                {/* Password Field with Strength Indicator */}
                                                <motion.div variants={itemVariants}>
                                                    <div className="relative">
                                                        <InputField
                                                            label="Password"
                                                            name="password"
                                                            type={hidePassword ? "password" : "text"}
                                                            placeholder="Enter your password"
                                                            required
                                                            isPassword={true}
                                                            showPassword={!hidePassword}
                                                            onPasswordToggle={togglePassword}
                                                            className="pl-12 py-4 rounded-2xl w-full bg-gray-50/80 border-gray-200 focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-300 text-base"
                                                        />
                                                        <div className="absolute bottom-0 left-4 top-6 flex items-center pointer-events-none">
                                                            <HiLockClosed className="h-5 w-5 text-gray-400" />
                                                        </div>
                                                    </div>
                                                    {/* Password strength indicator */}
                                                    {values.password && (
                                                        <motion.div
                                                            className="mt-2"
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                        >
                                                            <div className="flex gap-1 mb-1">
                                                                {[1, 2, 3, 4].map((level) => (
                                                                    <div
                                                                        key={level}
                                                                        className={`h-1 flex-1 rounded-full transition-colors duration-300 ${values.password.length >= level * 2
                                                                            ? values.password.length >= 8
                                                                                ? 'bg-green-500'
                                                                                : values.password.length >= 6
                                                                                    ? 'bg-yellow-500'
                                                                                    : 'bg-red-500'
                                                                            : 'bg-gray-200'
                                                                            }`}
                                                                    />
                                                                ))}
                                                            </div>
                                                            <span className={`text-xs ${values.password.length >= 8
                                                                ? 'text-green-600'
                                                                : values.password.length >= 6
                                                                    ? 'text-yellow-600'
                                                                    : 'text-red-600'
                                                                }`}>
                                                                {values.password.length >= 8
                                                                    ? 'Strong password'
                                                                    : values.password.length >= 6
                                                                        ? 'Good password'
                                                                        : 'Password too short'
                                                                }
                                                            </span>
                                                        </motion.div>
                                                    )}
                                                </motion.div>

                                                {/* Submit Button */}
                                                <motion.div
                                                    variants={buttonVariants}
                                                    whileHover="hover"
                                                    whileTap="tap"
                                                >
                                                    <Button
                                                        type="submit"
                                                        className="w-full bg-gradient-to-r from-primary to-green-600 hover:from-primary/90 hover:to-green-700 text-white font-semibold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-base disabled:opacity-70 disabled:cursor-not-allowed"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="flex items-center justify-center">
                                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Signing you in...
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center justify-center">
                                                                Sign In to Dashboard
                                                                <HiArrowRight className="ml-2 h-5 w-5" />
                                                            </span>
                                                        )}
                                                    </Button>
                                                </motion.div>

                                                {/* Security notice */}
                                                <motion.div
                                                    className="text-center pt-4"
                                                    variants={itemVariants}
                                                >
                                                    <p className="text-xs text-gray-500">
                                                        Protected by enterprise-grade security
                                                    </p>
                                                </motion.div>
                                            </Form>
                                        )}
                                    </Formik>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Footer */}
                        <motion.div
                            className="text-center mt-6 sm:mt-8"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.2, duration: 0.5 }}
                        >
                            <p className="text-xs text-gray-400 mt-1">
                                Powered by Rashail Infotech
                            </p>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Enhanced Right Panel with Better Responsive Design */}
                <motion.div
                    className="hidden lg:flex lg:w-1/2 relative z-10 overflow-hidden"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                >
                    {/* Enhanced gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-blue-500/15 to-indigo-600/20" />

                    {/* Geometric patterns */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-20 left-20 w-32 h-32 border border-white rounded-full" />
                        <div className="absolute bottom-32 right-16 w-24 h-24 border border-white rounded-lg rotate-45" />
                        <div className="absolute top-1/2 left-1/4 w-16 h-16 border border-white rounded-full" />
                    </div>

                    <motion.div
                        className="relative w-full flex flex-col items-center justify-center text-center p-12"
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        {/* Enhanced floating elements */}
                        <div className="absolute w-56 h-56 rounded-full bg-gradient-to-r from-blue-400/20 to-primary/30 blur-3xl -top-16 -right-16 animate-pulse" />
                        <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-primary/25 to-indigo-400/25 blur-3xl -bottom-24 -left-24 animate-pulse" style={{ animationDuration: '8s' }} />

                        {/* Content */}
                        <motion.div
                            className="relative z-10 max-w-md"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                        >
                            <img
                                src={loginImage}
                                alt="Seller Dashboard Illustration"
                                className="w-full max-w-sm mx-auto mb-8 drop-shadow-lg"
                            />

                            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                Manage Your Business
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                Access powerful tools to grow your business, track sales, manage inventory, and connect with customers.
                            </p>

                            {/* Feature highlights */}
                            <div className="space-y-3 text-left">
                                {[
                                    "Real-time sales analytics",
                                    "Inventory management",
                                    "Customer engagement tools",
                                    "Secure payment processing"
                                ].map((feature, index) => (
                                    <motion.div
                                        key={feature}
                                        className="flex items-center gap-3"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.5 + index * 0.1 }}
                                    >
                                        <div className="w-2 h-2 bg-primary rounded-full" />
                                        <span className="text-sm text-gray-600">{feature}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Enhanced light beams */}
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute w-full h-80 bg-white/10 rotate-12 transform -translate-x-1/2 -translate-y-full animate-beam" />
                            <div className="absolute w-full h-80 bg-white/5 -rotate-12 transform translate-x-1/2 -translate-y-full animate-beam" style={{ animationDelay: '3s' }} />
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            {/* Enhanced global styles */}
            <style jsx global>{`
                @keyframes beam {
                    0% { transform: translateY(-100%) rotate(12deg); }
                    50% { transform: translateY(50vh) rotate(12deg); }
                    100% { transform: translateY(150vh) rotate(12deg); }
                }
                .animate-beam {
                    animation: beam 12s infinite linear;
                    animation-delay: 2s;
                }
                
                /* Custom scrollbar for webkit browsers */
                ::-webkit-scrollbar {
                    width: 6px;
                }
                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }
                ::-webkit-scrollbar-thumb {
                    background: #2FB95D;
                    border-radius: 3px;
                }
                ::-webkit-scrollbar-thumb:hover {
                    background: #258a4a;
                }
                
                /* Smooth focus transitions */
                input:focus, button:focus {
                    transition: all 0.2s ease-in-out;
                }
                
                /* Mobile optimization */
                @media (max-width: 640px) {
                    .bg-white\/95 {
                        background-color: rgba(255, 255, 255, 0.98);
                    }
                }
                
                /* Prevent zoom on input focus for iOS */
                @media screen and (max-width: 767px) {
                    input[type="email"], input[type="password"], input[type="text"] {
                        font-size: 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Signin;